import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Slider,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import HLSPlayer from '../../../../components/common/hls/HLSPlayer';
import { downloadHlsAsWave, IssueCastChapter } from '../../../../api/hlsApiClient';
import ChaptersPanel from './ChaptersPanel';

export interface AudioPreviewResponse {
  description: string;
  audioData?: Blob | null;
  hlsUrl?: string;
  headers?: Headers;
  scriptData?: any;
  articleIds?: any;
  chapters?: Array<{
    title?: string;
    startTime?: number;
    runTimeSeconds?: number;
    linkToOriginal?: string;
    script?: {
      script?: Array<{ co_host?: string; line?: string }>;
      sections?: Array<{ type?: string; startIndex?: number; endIndex?: number }>;
    };
  }> | null;
  voiceText?: string;
  podcastName?: string;
  podcastDescription?: string;
  podcastPublishDate?: string;
  classification?: {
    incident_types?: string[];
    locations?: string[];
    severity_tags?: string[];
    timestamp?: string;
  };
}

interface AudioPreviewProps {
  buttonTitle: string;
  clickedButtonTitle: string;
  handleClickParam: any;
  handleClick: (param: any) => Promise<AudioPreviewResponse>;
  heading?: string;
  headingIcon?: React.ReactNode;
  disabled?: boolean;
  helperText?: string;
  downloadFilename?: string;
  websiteId?: string;
  websiteName?: string; // NEW: forward website name to ChaptersPanel
  locationName?: string; // NEW: forward location name to HLSPlayer for IssueCast publishing
  rightAdornment?: React.ReactNode; // NEW: content to render to the right of the button
  currentJobStep?: string; // NEW: optional description of the current job step (e.g., from StudioTab's job panel)
}

// Storage utilities for persistence
const STORAGE_PREFIX = 'location_station_audio_preview';
const STORAGE_VERSION = 'v1';
const STORAGE_EXPIRY_DAYS = 7; // Auto-expire stored data after 7 days

interface StoredAudioData {
  timestamp: number;
  response: AudioPreviewResponse;
  showPreview: boolean;
  chapters?: AudioPreviewResponse['chapters'];
  isHLSStream: boolean;
  audioUrl?: string | null;
  editedChapterLines?: Record<number, Array<{ co_host?: string; line?: string }>>;
}

const generateStorageKey = (param: any, websiteId?: string): string => {
  let key = `${STORAGE_PREFIX}_${STORAGE_VERSION}`;
  
  if (websiteId) {
    key += `_website_${websiteId}`;
  } else if (typeof param === 'object' && param !== null) {
    // Try to extract an ID from the param object
    const id = param.websiteId || param.websitePK || param.id;
    if (id) {
      key += `_param_${id}`;
    } else {
      // Create a hash from the stringified param for uniqueness
      try {
        const stringifiedParam = JSON.stringify(param);
        key += `_param_${stringifiedParam.length}_${stringifiedParam.slice(0, 20).replace(/[^a-zA-Z0-9]/g, '')}`;
      } catch (e) {
        key += '_generic';
      }
    }
  } else if (param) {
    // Use the param directly if it's a simple value
    key += `_param_${String(param).replace(/[^a-zA-Z0-9]/g, '')}`;
  } else {
    key += '_generic';
  }
  
  return key;
};

const saveToStorage = (key: string, data: StoredAudioData): void => {
  try {
    // Add timestamp for expiry tracking
    data.timestamp = Date.now();
    
    // Clean the data to ensure it's serializable
    const cleanData = JSON.parse(JSON.stringify(data));
    
    // Save to local storage
    localStorage.setItem(key, JSON.stringify(cleanData));
    
    // Clean up expired items
    cleanupStorage();
  } catch (error) {
    console.error('Failed to save audio preview state to local storage:', error);
  }
};

const loadFromStorage = (key: string): StoredAudioData | null => {
  try {
    const data = localStorage.getItem(key);
    if (!data) return null;
    
    const parsed = JSON.parse(data) as StoredAudioData;
    
    // Check if the data has expired
    const expiryTime = parsed.timestamp + (STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    if (Date.now() > expiryTime) {
      localStorage.removeItem(key);
      return null;
    }
    
    return parsed;
  } catch (error) {
    console.error('Failed to load audio preview state from local storage:', error);
    return null;
  }
};

const cleanupStorage = (): void => {
  try {
    // Find all keys that match our prefix
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        keys.push(key);
      }
    }
    
    // Check each key for expiration
    const now = Date.now();
    const expiryMs = STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    
    keys.forEach(key => {
      try {
        const data = localStorage.getItem(key);
        if (!data) return;
        
        const parsed = JSON.parse(data) as StoredAudioData;
        if (now - parsed.timestamp > expiryMs) {
          localStorage.removeItem(key);
        }
      } catch (e) {
        // If we can't parse the data, it's likely corrupted
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error cleaning up expired audio preview data:', error);
  }
};

export const getFileExtension = (blob: Blob): string => {
  const mimeType = blob.type;
  const mimeToExt: Record<string, string> = {
    'audio/wav': 'wav',
    'audio/x-wav': 'wav',
    'audio/wave': 'wav',
    'audio/mp3': 'mp3',
    'audio/mpeg': 'mp3',
    'audio/ogg': 'ogg',
    'audio/aac': 'aac',
    'audio/flac': 'flac',
    'audio/webm': 'webm'
  };
  
  return mimeToExt[mimeType] || 'wav';
};

const AudioPreview: React.FC<AudioPreviewProps> = ({
  buttonTitle,
  clickedButtonTitle,
  handleClickParam,
  handleClick,
  heading = "Audio Preview",
  headingIcon,
  disabled = false,
  helperText,
  downloadFilename,
  websiteId = '',
  websiteName, // NEW
  locationName, // NEW
  rightAdornment, // NEW
  currentJobStep, // NEW
}) => {
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [response, setResponse] = useState<AudioPreviewResponse | null>(null);
  const [chapters, setChapters] = useState<AudioPreviewResponse['chapters'] | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isHLSStream, setIsHLSStream] = useState(false);
  const [hlsError, setHlsError] = useState<{message: string; details?: string} | null>(null);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioEl, setAudioEl] = useState<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  
  // Storage key for persisting state
  const storageKey = useMemo(() => generateStorageKey(handleClickParam, websiteId), [handleClickParam, websiteId]);
  
  const [downloadFeedback, setDownloadFeedback] = useState<{
    show: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  }>({
    show: false,
    message: '',
    severity: 'info'
  });

  const [editedChapterLines, setEditedChapterLines] = useState<Record<number, Array<{ co_host?: string; line?: string }>>>({});
  
  // Add a state for restoring data notification
  const [restoredNotification, setRestoredNotification] = useState<boolean>(false);

  // NEW: detect if we already have audio with chapters loaded (from storage or current session)
  const hasExistingAudioAndChapters = useMemo(() => Array.isArray(chapters) && chapters.length > 0, [chapters]);
  const effectiveButtonTitle = hasExistingAudioAndChapters ? 'Regenerate full news podcast' : buttonTitle;
  const effectiveHelperText = hasExistingAudioAndChapters ? 'This will replace the existing audio.' : helperText;

  // Map chapters to IssueCastChapter format for HLSPlayer
  const mappedChapters: IssueCastChapter[] | undefined = useMemo(() => {
    if (!chapters) return undefined;
    return chapters.map(ch => ({
      title: ch.title || 'Untitled Chapter',
      startTime: ch.startTime || 0,
      runTimeSeconds: ch.runTimeSeconds || 0,
      linkToOriginal: ch.linkToOriginal || null,
      script: ch.script
    }));
  }, [chapters]);

  const formatTime = (seconds: number): string => {
    const safe = Number.isFinite(seconds) ? seconds : 0;
    const m = Math.floor(safe / 60);
    const s = Math.floor(safe % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // Load from local storage on component mount
  useEffect(() => {
    try {
      const storedData = loadFromStorage(storageKey);
      if (storedData) {
        setShowPreview(storedData.showPreview);
        setResponse(storedData.response);
        setChapters(storedData.chapters ?? storedData.response.chapters ?? null);
        setIsHLSStream(storedData.isHLSStream);
        
        if (storedData.audioUrl && !storedData.isHLSStream && storedData.response.audioData) {
          // We can't restore the exact Blob URL, so we need to create a new one
          const url = URL.createObjectURL(storedData.response.audioData);
          setAudioUrl(url);
        }
        
        if (storedData.editedChapterLines) {
          setEditedChapterLines(storedData.editedChapterLines);
        }
        
        // Show restored notification
        setRestoredNotification(true);
        setTimeout(() => setRestoredNotification(false), 3000);
      }
    } catch (error) {
      console.error('Error loading audio preview data from storage:', error);
    }
  }, [storageKey]);
  
  // Persist state changes to local storage
  const persistState = useCallback(() => {
    if (!response) return;
    
    const dataToStore: StoredAudioData = {
      timestamp: Date.now(),
      response,
      showPreview,
      chapters: chapters ?? response.chapters ?? null,
      isHLSStream,
      audioUrl,
      editedChapterLines,
    };
    
    saveToStorage(storageKey, dataToStore);
  }, [response, showPreview, chapters, isHLSStream, audioUrl, editedChapterLines, storageKey]);
  
  // Automatically persist state when key components change
  useEffect(() => {
    if (response) {
      persistState();
    }
  }, [response, chapters, editedChapterLines, persistState]);

  const persistChanges = useCallback((updatedEditedLines?: Record<number, Array<{ co_host?: string; line?: string }>>) => {
    if (!response) return;
    
    const dataToStore: StoredAudioData = {
      timestamp: Date.now(),
      response,
      showPreview,
      chapters: chapters ?? response.chapters ?? null,
      isHLSStream,
      audioUrl,
      editedChapterLines: updatedEditedLines ?? editedChapterLines,
    };
    
    saveToStorage(storageKey, dataToStore);
  }, [response, showPreview, chapters, isHLSStream, audioUrl, editedChapterLines, storageKey]);

  const onButtonClick = async () => {
    setLoading(true);
    setShowPreview(true);
    setElapsedTime(0);
    setError(null);
    setHlsError(null);
    setIsHLSStream(false);
    setAudioUrl(null);
    
    const start = Date.now();
    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    setTimerInterval(interval);
    
    try {
      const result = await handleClick(handleClickParam);
      console.log('Audio preview response:', result);
      setResponse(result);
      
      if (result.hlsUrl) {
        setIsHLSStream(true);
        setHlsError(null);
      } else if (result.audioData) {
        const url = URL.createObjectURL(result.audioData);
        setAudioUrl(url);
      } else {
        throw new Error('No audio data or HLS stream URL received');
      }
    } catch (error) {
      console.error('Error in audio preview:', error);
      setError('Failed to generate audio preview. Please try again.');
    } finally {
      if (interval) {
        clearInterval(interval);
        setTimerInterval(null);
      }
      
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (isHLSStream && response?.hlsUrl) {
      try {
        setDownloadFeedback({ show: true, message: 'Download started...', severity: 'info' });
        const wavBlob = await downloadHlsAsWave(response.hlsUrl);
        const wavUrl = URL.createObjectURL(wavBlob);
        const filename = downloadFilename ? `${downloadFilename}.wav` : `audio_preview_${new Date().toISOString().replace(/[:.]/g, '-')}.wav`;

        const downloadLink = document.createElement('a');
        downloadLink.href = wavUrl;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(wavUrl);

        setDownloadFeedback({ show: true, message: 'Download completed', severity: 'success' });
      } catch (error) {
        console.error('Error downloading HLS audio:', error);
        setDownloadFeedback({ show: true, message: 'Download failed. Please try again.', severity: 'error' });
      }
      return;
    }

    if (!response?.audioData || !audioUrl) {
      setDownloadFeedback({
        show: true,
        message: 'No audio available to download',
        severity: 'error'
      });
      return;
    }
    
    try {
      setDownloadFeedback({
        show: true,
        message: 'Download started...',
        severity: 'info'
      });
      
      const extension = getFileExtension(response.audioData);
      let filename;
      
      if (response.headers && response.headers.has('Content-Disposition')) {
        const contentDisposition = response.headers.get('Content-Disposition') || '';
        const filenameMatch = /filename[^;=\n]*=((['"]) .*?\2|[^;\n]*)/.exec(contentDisposition);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '').trim();
        }
      }
      
      if (!filename) {
        filename = downloadFilename 
          ? (downloadFilename.endsWith(`.${extension}`) ? downloadFilename : `${downloadFilename}.${extension}`)
          : `audio_preview_${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`;
      }
      
      const downloadLink = document.createElement('a');
      downloadLink.href = audioUrl;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      setTimeout(() => {
        setDownloadFeedback({
          show: true,
          message: 'Download completed',
          severity: 'success'
        });
      }, 1000);
    } catch (error) {
      console.error('Error downloading audio:', error);
      setDownloadFeedback({
        show: true,
        message: 'Download failed. Please try again.',
        severity: 'error'
      });
    }
  };
  
  const handleCloseFeedback = () => {
    setDownloadFeedback(prev => ({ ...prev, show: false }));
  };

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const handleClose = () => {
    setShowPreview(false);
    setResponse(null);
    setError(null);
    setIsHLSStream(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleTimeChange = (event: Event, newValue: number | number[]) => {
    if (audioRef.current && typeof newValue === 'number') {
      audioRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  const updateAudioTime = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      setCurrentTime(audio.currentTime);
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    }
  }, []);

  const handlePlay = useCallback(() => setIsPlaying(true), []);
  const handlePause = useCallback(() => setIsPlaying(false), []);
  const handleEnded = handlePause;

  useEffect(() => {
    const audio = audioEl;
    if (!audio) return;

    audio.addEventListener('timeupdate', updateAudioTime);
    audio.addEventListener('loadedmetadata', updateAudioTime);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateAudioTime);
      audio.removeEventListener('loadedmetadata', updateAudioTime);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioEl, updateAudioTime, handlePlay, handlePause, handleEnded]);

  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  const handleHLSError = (error: Error) => {
    console.error('HLS Player error:', error);
    
    // Extract detailed error information if available
    let errorDetails = '';
    let errorMessage = error.message || 'Unknown error occurred';
    
    // Try to get response details attached to the error
    const responseText = (error as any).responseText || '';
    if ('response' in error && error.response) {
      const responseData = typeof error.response === 'string' ? error.response : JSON.stringify(error.response, null, 2);
      errorDetails = responseData;
      
      // Parse AWS S3 XML error response if present
      if ((responseText.includes('<Error>') && responseText.includes('AccessDenied')) || 
          (responseData.includes('<Error>') && responseData.includes('AccessDenied'))) {
        try {
          const xmlText = responseText || responseData;
          // Extract expiration time from XML
          const expiresMatch = xmlText.match(/<Expires>(.*?)<\/Expires>/);
          const messageMatch = xmlText.match(/<Message>(.*?)<\/Message>/);
          
          if (expiresMatch && expiresMatch[1]) {
            const expiresDate = new Date(expiresMatch[1]);
            const localTimeString = expiresDate.toLocaleString('en-US', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              second: '2-digit',
              timeZoneName: 'short'
            });
            
            errorMessage = `The audio file URL expired at ${localTimeString}`;
            
            if (messageMatch && messageMatch[1]) {
              errorMessage += ` (${messageMatch[1]})`;
            }
          } else if (messageMatch && messageMatch[1]) {
            errorMessage = messageMatch[1];
          }
        } catch (parseError) {
          console.warn('Failed to parse AWS error response:', parseError);
        }
      }
    } else if (responseText) {
      // Check responseText directly
      errorDetails = responseText;
      
      if (responseText.includes('<Error>') && responseText.includes('AccessDenied')) {
        try {
          const expiresMatch = responseText.match(/<Expires>(.*?)<\/Expires>/);
          const messageMatch = responseText.match(/<Message>(.*?)<\/Message>/);
          
          if (expiresMatch && expiresMatch[1]) {
            const expiresDate = new Date(expiresMatch[1]);
            const localTimeString = expiresDate.toLocaleString('en-US', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              second: '2-digit',
              timeZoneName: 'short'
            });
            
            errorMessage = `The audio file URL expired at ${localTimeString}`;
            
            if (messageMatch && messageMatch[1]) {
              errorMessage += ` (${messageMatch[1]})`;
            }
          } else if (messageMatch && messageMatch[1]) {
            errorMessage = messageMatch[1];
          }
        } catch (parseError) {
          console.warn('Failed to parse AWS error response:', parseError);
        }
      }
    } else if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
      errorDetails = 'HTTP 403 - Access Denied. The audio file URL may have expired.';
    } else if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
      errorDetails = 'HTTP 404 - Audio file not found.';
    }
    
    setHlsError({
      message: errorMessage,
      details: errorDetails
    });
  };

  useEffect(() => {
    setChapters(response?.chapters ?? null);
  }, [response?.chapters]);

  // Regenerate handler passed to ChaptersPanel
  const handleRegenerate = async (chs: NonNullable<AudioPreviewResponse['chapters']>, classification?: AudioPreviewResponse['classification']) => {
    if (!chs || !handleClickParam) return;
    setLoading(true);
    setError(null);

    try {
      const result = await handleClick({
        ...handleClickParam,
        chapters: chs,
        classification,
      });

      setResponse(result);

      if (result.hlsUrl) {
        setIsHLSStream(true);
      } else if (result.audioData) {
        const url = URL.createObjectURL(result.audioData);
        setAudioUrl(url);
      } else {
        throw new Error('No audio data or HLS stream URL received');
      }

      setDownloadFeedback({ show: true, message: 'Audio successfully regenerated with your edits', severity: 'success' });
    } catch (e) {
      console.error('Error regenerating audio with edited chapters:', e);
      setError('Failed to regenerate audio with your edits. Please try again.');
      setDownloadFeedback({ show: true, message: 'Failed to regenerate audio', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'nowrap' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
            onClick={onButtonClick}
            disabled={disabled || loading}
          >
            {loading ? clickedButtonTitle : effectiveButtonTitle}
          </Button>
          {rightAdornment ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {rightAdornment}
            </Box>
          ) : null}
        </Box>
      </Box>
      
      {effectiveHelperText && (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          {effectiveHelperText}
        </Typography>
      )}
      
      {showPreview && (
        <Paper elevation={1} sx={{ mt: 2, p: 2, position: 'relative' }}>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ position: 'absolute', top: 8, right: 8 }}
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {headingIcon && <Box sx={{ mr: 1 }}>{headingIcon}</Box>}
            <Typography variant="h6" gutterBottom>
              {heading}
            </Typography>
          </Box>
          
          {loading ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {currentJobStep && currentJobStep.trim().length > 0
                  ? currentJobStep
                  : 'Generating audio preview... Please wait.'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Time elapsed: {formatTime(elapsedTime)}
              </Typography>
            </>
          ) : error ? (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          ) : response ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {response.description}
              </Typography>
              <Box sx={{ mt: 2 }}>
                {isHLSStream ? (
                  <Box sx={{ width: '100%' }}>
                    {hlsError ? (
                      // Show error details instead of the player
                      <Box sx={{ 
                        p: 3, 
                        bgcolor: 'error.light', 
                        color: 'error.contrastText',
                        borderRadius: 1,
                        border: (theme) => `2px solid ${theme.palette.error.main}`
                      }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Audio Stream Error
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {hlsError.message}
                        </Typography>
                        {hlsError.details && (
                          <Box sx={{ 
                            mt: 2, 
                            p: 2, 
                            bgcolor: 'rgba(0,0,0,0.1)', 
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            overflow: 'auto',
                            maxHeight: '200px'
                          }}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                              Error Details:
                            </Typography>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {hlsError.details}
                            </pre>
                          </Box>
                        )}
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ color: 'info.contrastText' }}>
                            <strong>How to fix:</strong> You can recreate the audio by clicking the <strong>"Apply Edits To Audio"</strong> button below in the chapters panel, or regenerate the full podcast using the button above.
                          </Typography>
                        </Box>
                      </Box>
                    ) : response.hlsUrl ? (
                      <>
                        <HLSPlayer
                          src={response.hlsUrl}
                          autoPlay={false}
                          muted={false}
                          width="100%"
                          height="auto"
                          className="audio-preview-hls"
                          onError={handleHLSError}
                          hlsConfig={{
                            startPosition: -1,
                            liveSyncDurationCount: 0,
                            liveMaxLatencyDurationCount: Infinity,
                            initialLiveManifestSize: 1,
                          }}
                          websiteId={websiteId || (typeof handleClickParam === 'object' && handleClickParam ? (handleClickParam.websiteId || handleClickParam.websitePK || handleClickParam.id || '') : '')}
                          audioOnly
                          initialName={response.podcastName}
                          initialDescription={response.podcastDescription}
                          initialPublishDate={response.podcastPublishDate}
                          chapters={mappedChapters}
                          articleIds={response.articleIds}
                          locationName={locationName}
                          podcastType={(handleClickParam as any)?.podcastFormat === 'emergency' ? 'emergency' : 'multiple_articles'}
                          classification={response.classification}
                        />
                        {!hlsError && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <IconButton
                              onClick={handleDownload}
                              color="primary"
                              aria-label="download audio"
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Box>
                        )}
                      </>
                    ) : null}
                  </Box>
                ) : (
                  <>
                    <audio 
                      ref={(el) => { 
                        audioRef.current = el; 
                        setAudioEl(el);
                      }}
                      src={audioUrl || undefined} 
                      style={{ display: 'none' }}
                    >
                      Your browser does not support the audio element.
                    </audio>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <IconButton onClick={togglePlayPause} color="primary">
                        {isPlaying ? <Pause /> : <PlayArrow />}
                      </IconButton>
                      <Typography variant="body2" sx={{ minWidth: 50 }}>
                        {formatTime(currentTime)}
                      </Typography>
                      <Slider
                        size="small"
                        value={currentTime}
                        max={duration || 100}
                        onChange={handleTimeChange}
                        aria-label="Audio progress"
                        sx={{ mx: 2 }}
                      />
                      <Typography variant="body2" sx={{ minWidth: 50 }}>
                        {formatTime(duration)}
                      </Typography>
                      
                      <IconButton 
                        onClick={handleDownload} 
                        color="primary" 
                        aria-label="download audio"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Box>
                  </>
                )}
              </Box>

              {(chapters || response.scriptData || response.articleIds) && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2, border: (theme) => `1px solid ${theme.palette.divider}` }}>
                  {Array.isArray(chapters) && chapters.length > 0 ? (
                    <>
                      <ChaptersPanel
                        chapters={chapters}
                        onChaptersChange={(chs) => {
                          setChapters(chs);
                          setResponse((prev) => (prev ? { ...prev, chapters: chs } : prev));
                        }}
                        editedLines={editedChapterLines}
                        onEditedLinesChange={(map) => {
                          setEditedChapterLines(map);
                          persistChanges(map);
                        }}
                        onRegenerate={handleRegenerate}
                        disabled={disabled}
                        loading={loading}
                        onNotify={(message, severity = 'info') => setDownloadFeedback({ show: true, message, severity })}
                        websiteName={websiteName}
                        websiteId={websiteId || (typeof handleClickParam === 'object' && handleClickParam ? (handleClickParam.websiteId || handleClickParam.websitePK || handleClickParam.id || '') : '')}
                        classification={response.classification}
                      />
                    </>
                  ) : null}

                  {!chapters && response.scriptData && (
                    <>
                    </>
                  )}
                  {response.articleIds && (
                    <>
                    </>
                  )}
                </Box>
              )}
            </>
          ) : null}
        </Paper>
      )}
      
      <Snackbar
        open={downloadFeedback.show}
        autoHideDuration={3000}
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseFeedback} 
          severity={downloadFeedback.severity}
          sx={{ width: '100%' }}
        >
          {downloadFeedback.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={restoredNotification}
        autoHideDuration={3000}
        onClose={() => setRestoredNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setRestoredNotification(false)} 
          severity="info"
          sx={{ width: '100%' }}
        >
          Restored previous session data.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AudioPreview;